/**
 * Utility function to create a cropped image file from a source image
 * Uses HTML5 Canvas API to perform client-side image cropping
 * Works with react-easy-crop's pixel area coordinates
 */
interface Area {
  x: number
  y: number
  width: number
  height: number
}

export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area
): Promise<File> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.src = imageSrc

    image.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        reject(new Error('Could not get canvas context'))
        return
      }

      // Set canvas size to crop dimensions
      canvas.width = pixelCrop.width
      canvas.height = pixelCrop.height

      // Draw the cropped image
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      )

      // Convert canvas to blob, then to File
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob from canvas'))
            return
          }

          // Create a File from the blob
          const file = new File([blob], 'cropped-image.jpg', {
            type: 'image/jpeg',
            lastModified: Date.now()
          })
          resolve(file)
        },
        'image/jpeg',
        0.95 // Quality
      )
    }

    image.onerror = () => {
      reject(new Error('Failed to load image'))
    }
  })
}

